import { getTableCover, getTableCovers } from '@/lib/api/tableCoverApi'
import isNotNil from '@/lib/utils/isNotNil'
import { BaseFileEnum, type FolderCoverFragment, type FormCoverFragment } from '@/typings/domain'
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type Draft,
  type PayloadAction,
} from '@reduxjs/toolkit'
import type { RootState } from '../store'
import type { QueryState } from './queryStateType'

export type TableCover = FormCoverFragment | FolderCoverFragment

export enum TableCoversFilterKey {
  MyTables = 'home',
  SharedTables = 'shared_forms',
}

type PageInfo = {
  hasNextPage: boolean
  endCursor?: string | null
}

type TableCoversMeta = { coverIds: string[] } & QueryState & { pageInfo: PageInfo }
type TableCoversMetaByBaseFileType = Record<BaseFileEnum, TableCoversMeta>

type TableCoversMetaState = {
  activeFilter: TableCoversFilterKey
  metaByFilter: Record<TableCoversFilterKey, TableCoversMetaByBaseFileType>
  metaByFolderToken: Record<string, TableCoversMetaByBaseFileType>
  metaOfSearch: TableCoversMetaByBaseFileType
}

const tableCoverAdapter = createEntityAdapter<TableCover, string>({
  selectId: cover => cover.token,
})

function buildInitialMeta(): TableCoversMeta {
  return {
    coverIds: [],
    isLoading: false,
    loaded: false,
    error: null,
    pageInfo: {
      hasNextPage: false,
      endCursor: '',
    },
  }
}

function buildInitialMetaByBaseFileType(): TableCoversMetaByBaseFileType {
  return {
    [BaseFileEnum.Form]: buildInitialMeta(),
    [BaseFileEnum.FormFolder]: buildInitialMeta(),
  }
}

function updateMetaStateOnFetchingCoversFulfilled(
  metaState: Draft<TableCoversMeta>,
  payload: FetchTableCoversThunkResult,
  reset?: boolean
) {
  const coverIds = payload.covers.map(cover => cover.token)
  metaState.coverIds = reset ? coverIds : [...metaState.coverIds, ...coverIds]
  metaState.loaded = true
  metaState.isLoading = false
  metaState.pageInfo = payload.pageInfo
}

export const tableCoverSlice = createSlice({
  name: 'tableCover',
  initialState: tableCoverAdapter.getInitialState<TableCoversMetaState>({
    activeFilter: TableCoversFilterKey.MyTables,
    metaByFilter: {
      [TableCoversFilterKey.MyTables]: buildInitialMetaByBaseFileType(),
      [TableCoversFilterKey.SharedTables]: buildInitialMetaByBaseFileType(),
    },
    metaByFolderToken: {},
    metaOfSearch: buildInitialMetaByBaseFileType(),
  }),
  reducers: {
    setActiveFilter: (state, action: PayloadAction<TableCoversFilterKey>) => {
      state.activeFilter = action.payload
    },
    resetTableCoversMetaStateOfSearch: state => {
      state.metaOfSearch = buildInitialMetaByBaseFileType()
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTableCoversByFilterThunk.pending, (state, action) => {
        const { type, filter } = action.meta.arg
        state.metaByFilter[filter][type].isLoading = true
      })
      .addCase(fetchTableCoversByFilterThunk.fulfilled, (state, action) => {
        tableCoverAdapter.upsertMany(state, action.payload.covers)
        const { type, filter, reset } = action.meta.arg
        const metaState = state.metaByFilter[filter][type]
        updateMetaStateOnFetchingCoversFulfilled(metaState, action.payload, reset)
      })
      .addCase(fetchTableCoversByFilterThunk.rejected, (state, action) => {
        const { type, filter } = action.meta.arg
        const metaState = state.metaByFilter[filter][type]
        metaState.error = action.error.message ?? 'Failed to fetch table covers'
        metaState.isLoading = false
      })
      .addCase(fetchTableCoversByFolderThunk.pending, (state, action) => {
        const { type, folderToken } = action.meta.arg
        if (!state.metaByFolderToken[folderToken]) {
          state.metaByFolderToken[folderToken] = buildInitialMetaByBaseFileType()
        }
        state.metaByFolderToken[folderToken][type].isLoading = true
      })
      .addCase(fetchTableCoversByFolderThunk.fulfilled, (state, action) => {
        tableCoverAdapter.upsertMany(state, action.payload.covers)
        const { type, folderToken, reset } = action.meta.arg
        const metaState = state.metaByFolderToken[folderToken]?.[type]
        if (metaState) {
          updateMetaStateOnFetchingCoversFulfilled(metaState, action.payload, reset)
        }
      })
      .addCase(fetchTableCoversByFolderThunk.rejected, (state, action) => {
        const { type, folderToken } = action.meta.arg
        const metaState = state.metaByFolderToken[folderToken]?.[type]
        if (metaState) {
          metaState.error = action.error.message ?? 'Failed to fetch table covers'
          metaState.isLoading = false
        }
      })
      .addCase(fetchTableCoversByKeywordThunk.pending, (state, action) => {
        const { type } = action.meta.arg
        state.metaOfSearch[type].isLoading = true
      })
      .addCase(fetchTableCoversByKeywordThunk.fulfilled, (state, action) => {
        tableCoverAdapter.upsertMany(state, action.payload.covers)
        const { type, reset } = action.meta.arg
        const metaState = state.metaOfSearch[type]
        updateMetaStateOnFetchingCoversFulfilled(metaState, action.payload, reset)
      })
      .addCase(fetchTableCoversByKeywordThunk.rejected, (state, action) => {
        const { type } = action.meta.arg
        const metaState = state.metaOfSearch[type]
        metaState.error = action.error.message ?? 'Failed to fetch table covers'
        metaState.isLoading = false
      })
      .addCase(fetchTableCoverThunk.fulfilled, (state, action) => {
        tableCoverAdapter.upsertOne(state, action.payload)
      })
  },
})

function pageSizeFor(type: BaseFileEnum) {
  return type === BaseFileEnum.FormFolder ? 100 : 20
}

type FetchTableCoversByFilterThunkArg = {
  type: BaseFileEnum
  filter: TableCoversFilterKey
  reset?: boolean
}
type FetchTableCoversThunkResult = {
  covers: TableCover[]
  pageInfo: PageInfo
}
export const fetchTableCoversByFilterThunk = createAsyncThunk<
  FetchTableCoversThunkResult,
  FetchTableCoversByFilterThunkArg,
  { state: RootState }
>('tableCover/fetchByFilter', async ({ type, filter, reset }, thunkAPI) => {
  const after = reset ? null : thunkAPI.getState().tableCover.metaByFilter[filter][type].pageInfo.endCursor

  const { data } = await getTableCovers({
    tagId: filter === TableCoversFilterKey.MyTables ? null : filter,
    sort: 'created_at',
    first: pageSizeFor(type),
    type,
    after,
  })

  return data.tableCovers?.nodes && data.tableCovers?.pageInfo
    ? { covers: data.tableCovers.nodes.filter(isNotNil) as TableCover[], pageInfo: data.tableCovers.pageInfo }
    : thunkAPI.rejectWithValue({ message: 'Failed to fetch table covers' })
})

type FetchTableCoversByFolderThunkArg = {
  type: BaseFileEnum
  folderToken: string
  reset?: boolean
}
export const fetchTableCoversByFolderThunk = createAsyncThunk<
  FetchTableCoversThunkResult,
  FetchTableCoversByFolderThunkArg,
  { state: RootState }
>('tableCover/fetchByFolder', async ({ type, folderToken, reset }, thunkAPI) => {
  const after = reset
    ? null
    : thunkAPI.getState().tableCover.metaByFolderToken[folderToken]?.[type].pageInfo.endCursor

  const { data } = await getTableCovers({ folderId: folderToken, first: pageSizeFor(type), type, after })
  return data.tableCovers?.nodes && data.tableCovers?.pageInfo
    ? { covers: data.tableCovers.nodes.filter(isNotNil) as TableCover[], pageInfo: data.tableCovers.pageInfo }
    : thunkAPI.rejectWithValue({ message: 'Failed to fetch table covers' })
})

type FetchTableCoversByKeywordThunkArg = {
  keyword: string
  type: BaseFileEnum
  reset?: boolean
}
export const fetchTableCoversByKeywordThunk = createAsyncThunk<
  FetchTableCoversThunkResult,
  FetchTableCoversByKeywordThunkArg,
  { state: RootState }
>('tableCover/fetchByKeyword', async ({ keyword, type, reset }, thunkAPI) => {
  const after = reset ? null : thunkAPI.getState().tableCover.metaOfSearch[type].pageInfo.endCursor

  const { data } = await getTableCovers({ keyword, first: pageSizeFor(type), type, after })
  return data.tableCovers?.nodes && data.tableCovers?.pageInfo
    ? { covers: data.tableCovers.nodes.filter(isNotNil) as TableCover[], pageInfo: data.tableCovers.pageInfo }
    : thunkAPI.rejectWithValue({ message: 'Failed to fetch table covers' })
})

export const fetchTableCoverThunk = createAsyncThunk<FormCoverFragment, { tableToken: string }>(
  'tableCover/fetchOne',
  async ({ tableToken }, thunkAPI) => {
    const { data } = await getTableCover({ tableToken })
    return data.form ?? thunkAPI.rejectWithValue({ message: 'Failed to fetch table cover' })
  }
)

export type TableCoversFetchingKey =
  | { filter: TableCoversFilterKey; folderToken?: never; byKeyword?: never }
  | { folderToken: string; filter?: never; byKeyword?: never }
  | { byKeyword: true; filter?: never; folderToken?: never }

export const selectTableCoversMetaState = createSelector(
  (state: RootState) => state.tableCover,
  (_: RootState, key: TableCoversFetchingKey) => key,
  (_: RootState, _key: TableCoversFetchingKey, type: BaseFileEnum) => type,
  (tableCoverState, { filter, folderToken, byKeyword }, type) => {
    if (byKeyword) {
      return tableCoverState.metaOfSearch[type]
    }
    if (filter) {
      return tableCoverState.metaByFilter[filter][type]
    }
    return tableCoverState.metaByFolderToken[folderToken]?.[type] ?? buildInitialMeta()
  }
)

export const { selectAll: selectAllTableCovers, selectById: selectTableCoverById } = tableCoverAdapter.getSelectors()
export const { setActiveFilter, resetTableCoversMetaStateOfSearch } = tableCoverSlice.actions

export default tableCoverSlice.reducer
